import { create } from 'zustand';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  /**
   * The brand's IANA time zone (onboarding, brand details): its posts are
   * scheduled on this clock, and the calendar and the planner work in it (PC-11).
   */
  timezone?: string;
  /** The account the brand belongs to (ADR-006): the account holds the plan. */
  accountId?: string;
  /** The Supabase user id of the account owner. */
  ownerId?: string;
  /**
   * The ACCOUNT's subscription: the same object on every brand of the account
   * (ADR-006). STARTER, CLASSIC (grandfathered), PRO or AGENCY. Read the plan
   * in force through useAccountState() or effectivePlan(), never `plan`
   * directly.
   */
  subscription?: {
    plan: 'STARTER' | 'CLASSIC' | 'PRO' | 'AGENCY';
    status?: string;
    currentPeriodEnd?: string | null;
    /** The end of an admin's timed comp (status 'trialing'); no sign-up gets a trial. */
    trialEndsAt?: string | null;
    extraBrands?: number;
  } | null;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  /** An ownership transfer waiting for this user id to accept (AL-20), and when it was asked. */
  pendingOwnerId?: string | null;
  transferRequestedAt?: string | null;
  /** Set by the onboarding wizard; null on a brand nobody has set up yet. */
  industry?: string | null;
  /** ISO timestamp of when the brand was created. */
  createdAt?: string;
}

// The API client must send X-Workspace-Id on every request, but the store is
// empty on a fresh load until /workspaces/me resolves. Persisting the last
// selected id lets the request interceptor attach it immediately, so actions
// fired during that hydration window (e.g. clicking "Sync") don't 403.
const WORKSPACE_ID_KEY = 'unipostudio.workspaceId';

export function getPersistedWorkspaceId(): string | null {
  try {
    return localStorage.getItem(WORKSPACE_ID_KEY);
  } catch {
    return null;
  }
}

/** Remembers a brand as the one to open next (an accepted invitation). */
export function persistWorkspaceId(id: string): void {
  try {
    localStorage.setItem(WORKSPACE_ID_KEY, id);
  } catch {
    /* storage unavailable: the brand list picks the first brand */
  }
}

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setCurrentWorkspace: (workspace: Workspace) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  workspaces: [],
  currentWorkspace: null,
  isLoading: true,

  setWorkspaces: (workspaces) =>
    set({ workspaces }),

  setCurrentWorkspace: (workspace) => {
    try {
      localStorage.setItem(WORKSPACE_ID_KEY, workspace.id);
    } catch {
      /* storage unavailable — the store value still works within the session */
    }
    set({ currentWorkspace: workspace });
  },

  setLoading: (isLoading) =>
    set({ isLoading }),

  reset: () => {
    try {
      localStorage.removeItem(WORKSPACE_ID_KEY);
    } catch {
      /* ignore */
    }
    set({ workspaces: [], currentWorkspace: null, isLoading: false });
  },
}));
